import { cache } from "@emotion/css"; // eslint-disable-line @emotion/no-vanilla
import { CacheProvider, Global } from "@emotion/react";
import { NoSsr } from "@mui/base";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { postiesPageTypes, theme } from "@product/scmp-sdk";
import { getFrontendFlags } from "@unleash/nextjs";
import { FlagProvider } from "@unleash/nextjs/client";
import { AnimatePresence, domAnimation, LazyMotion } from "framer-motion";
import { Provider as JotaiProvider } from "jotai";
import type { NextApp } from "next";
import NextApp_ from "next/app";
import type { AppContext } from "next/app";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { RelayEnvironmentProvider } from "react-relay";
import { useRelayNextjs } from "relay-nextjs/app";
import { SWRConfig } from "swr";

import { AdvertisementScripts } from "~/components/advertisement";
import { AppLinkingData } from "~/components/app-linking-data";
import { IdleQueue } from "~/components/common/idle-queue";
import { PageTransition } from "~/components/common/page-transition";
import { GoogleOneTapLoginContainer } from "~/components/google-one-tap-login";
import { TrackingScripts, useInitTrackingSetup } from "~/components/tracking";
import { OnePlusXScripts } from "~/components/tracking/one-plus-x";
import { config } from "~/data";
import type { PageProps } from "~/lib/relay-nextjs";
import { createClientEnvironmentByPageProps } from "~/lib/relay-nextjs/helpers";
import { useCurrentPageType } from "~/lib/router/hooks";
import { globalCss } from "~/lib/styles/global-styles";
import { isExternalScriptsDisabled } from "~/lib/utils";

import { FooterElementID } from "./_app/consts";
import {
  DynamicAppBar,
  DynamicC2paProvider,
  DynamicContentReactionActionRedirect,
  DynamicHamburgerMenu,
  DynamicLoginDialog,
  DynamicMixpanelProvider,
  DynamicNewsTicker,
  DynamicPopupContainer,
  DynamicRosettaContextProvider,
  DynamicSdkContextProvider,
  DynamicSimpleHooksRunner,
  DynamicSnackbarProvider,
} from "./_app/dynamics";
import { useDisableScrollRestoration } from "./_app/hooks";
import { Content, FooterContainer, PageContainer } from "./_app/styles";

const App: NextApp<PageProps> = ({ Component, err, pageProps, toggles }) => {
  const { env: environment, ...relayProps } = useRelayNextjs(pageProps, {
    createClientEnvironment: () => createClientEnvironmentByPageProps(pageProps),
  });

  const currentPageType = useCurrentPageType();
  const isPostiesPageTypes = postiesPageTypes.includes(currentPageType);

  useInitTrackingSetup();

  useDisableScrollRestoration();

  // For supporting layout per page
  const renderLayout = () => {
    const PageComponent = <Component {...pageProps} {...relayProps} err={err} />;
    if (Component.getLayout) {
      return Component.getLayout(PageComponent);
    }

    return (
      <PageContainer
        $contentResponsivePadding={pageProps.contentConfiguration?.responsivePadding}
        $pageResponsivePadding={pageProps.pageConfiguration?.responsivePadding}
      >
        <IdleQueue>
          <DynamicPopupContainer />
          <DynamicHamburgerMenu />
        </IdleQueue>
        <NoSsr>
          <DynamicSimpleHooksRunner />
          <DynamicContentReactionActionRedirect />
          <DynamicLoginDialog />
        </NoSsr>
        <DynamicAppBar pageProps={pageProps}>
          <NoSsr>
            <IdleQueue>
              {!isPostiesPageTypes && <GoogleOneTapLoginContainer />}
              <DynamicNewsTicker appBarVariant={pageProps?.appBarConfiguration?.variant} />
            </IdleQueue>
          </NoSsr>
        </DynamicAppBar>
        <Content>
          <AnimatePresence initial={false} mode="wait">
            <PageTransition animateKey={pageProps.animateKey}>{PageComponent}</PageTransition>
          </AnimatePresence>
        </Content>
        <FooterContainer id={FooterElementID} />
      </PageContainer>
    );
  };
  return (
    <FlagProvider config={{ bootstrap: toggles, refreshInterval: 3600 }}>
      <AppLinkingData />
      {/* Moved to worker thread can't toggle by query params */}
      <OnePlusXScripts />
      <Global styles={globalCss} />
      <LazyMotion features={domAnimation}>
        <MuiThemeProvider theme={theme}>
          <JotaiProvider>
            {!isExternalScriptsDisabled() && (
              <>
                <AdvertisementScripts />
                <TrackingScripts />
              </>
            )}

            <SWRConfig
              value={{
                onErrorRetry: (_error, _key, _config, revalidate, { retryCount }) => {
                  if (retryCount >= 5) return;
                  setTimeout(() => revalidate({ retryCount }), 5000);
                },
                provider: () => new Map(),
              }}
            >
              <RelayEnvironmentProvider environment={environment}>
                <DynamicSnackbarProvider>
                  <DynamicMixpanelProvider>
                    <DynamicSdkContextProvider>
                      <DynamicRosettaContextProvider>
                        <DynamicC2paProvider>
                          <GoogleReCaptchaProvider reCaptchaKey={config.account.recaptchaSiteKey}>
                            <CacheProvider value={cache}>{renderLayout()}</CacheProvider>
                          </GoogleReCaptchaProvider>
                        </DynamicC2paProvider>
                      </DynamicRosettaContextProvider>
                    </DynamicSdkContextProvider>
                  </DynamicMixpanelProvider>
                </DynamicSnackbarProvider>
              </RelayEnvironmentProvider>
            </SWRConfig>
          </JotaiProvider>
        </MuiThemeProvider>
      </LazyMotion>
    </FlagProvider>
  );
};

App.getInitialProps = async (context_: AppContext) => {
  const initialProps = await NextApp_.getInitialProps(context_);
  try {
    const { toggles } = await getFrontendFlags();
    return {
      ...initialProps,
      toggles,
    };
  } catch {
    return initialProps;
  }
};

App.displayName = "App";

export default App;
